import { Row } from "antd"
import React, { Ref, RefObject } from "react"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import Button from "components/Button/Button/Index"
import { Buttons } from "components/Card/Card"
import { GenericModal } from "components/Modal/Modal"
import { Call } from "types/payload"
import { ModalProps } from "types/props"
import { ClientStore, DocumentsStore } from "types/store"

import { actions } from "./actions"
import { useHasCheckupDocument } from "./checkup"
import { useLanguage } from "locales"
import { DOCUMENT_NOT_FOUND_PAGE } from "core/constants"

// Printing is retried while the PDF is not yet rendered in the iframe, but a
// document on a shared pharmacy terminal must never be printed in a loop:
// both retry paths below are bounded.
const MAX_PRINT_ATTEMPTS = 5
const MAX_PRINT_INTERVAL_TICKS = 3

export const hasDocuments = (call: Call): boolean => {
  if (
    !call.is_prescription &&
    !call.is_report &&
    !call.is_workleave &&
    !call.is_certificate
  ) {
    return false
  }
  return true
}

export const ReadableDocument = React.forwardRef(
  (
    props: {
      documentUrl?: string
      setPrinted?: (arg: boolean) => void
      isPrinted: boolean
    },
    ref: Ref<HTMLIFrameElement>,
  ): JSX.Element =>
    props.documentUrl ? (
      <iframe
        title="readable-document"
        className="iframe-document"
        id="iFrame"
        name="iFrame"
        src={`${props.documentUrl}#toolbar=0`}
        ref={ref}
        onLoad={(win: any) => {
          try {
            if (
              props.isPrinted ||
              props.documentUrl === DOCUMENT_NOT_FOUND_PAGE
            )
              return
            win.target?.contentWindow?.print()
            props.setPrinted?.(true)
          } catch (error) {
            console.error(error, {
              path: "ReadableDocument::onLoad::print",
            })
          }
        }}
      />
    ) : (
      <div className="iframe-document"></div>
    ),
)

const handlePrintDocument = (
  iframeRef: RefObject<HTMLIFrameElement>,
  setLoading: (arg: boolean) => void,
  setPrinted: (arg: boolean) => void,
  attempt = 1,
) => {
  setLoading(false)
  const ifrm = iframeRef?.current as HTMLIFrameElement
  const win: Window | null = ifrm?.contentWindow || null
  const doc: Document | null = ifrm?.contentDocument || null
  // if the pdf takes up space in the body, we print
  // By default , body empty has a length equal to 13 in my case
  const src = iframeRef.current?.getAttribute("src") || ""
  // Split is needed because we append #toolbar=0
  const baseSrc = src.split("#")[0]
  if (
    win &&
    typeof win.onload !== "undefined" &&
    baseSrc &&
    baseSrc !== DOCUMENT_NOT_FOUND_PAGE
  ) {
    win.print()
    setPrinted(true)
    setTimeout(() => {
      setLoading(true)
    }, 1000)
  } else {
    if (!doc) return
    if (attempt >= MAX_PRINT_ATTEMPTS) {
      // Give up: the "Imprimer" button in the modal lets the patient retry.
      console.error("Could not print document after retries", {
        path: "document::handlePrintDocument",
      })
      return
    }
    setTimeout(() => {
      handlePrintDocument(iframeRef, setLoading, setPrinted, attempt + 1)
    }, 1000)
  }
}

export const ModalDocument = ({
  call,
  hideSendButton,
  ...rest
}: Partial<ModalProps> & { call?: Call; hideSendButton?: boolean }): JSX.Element => {
  const { languages } = useLanguage()
  const dispatch = useDispatch()
  const { documentUrl, callId, ...props } = useSelector(
    ({
      client,
      documents,
    }: {
      client: ClientStore
      documents: DocumentsStore
    }) => ({
      documentUrl: call && client.documentUrl,
      callId: client.call?.id || -1,
      ...documents,
    }),
  )
  const [printed, setPrinted] = useState<boolean>(false)
  const [printedModal, setPrintedModal] = useState<boolean>(false)
  const [printInterval, setPrintInterval] = useState<any>(null)

  const [visible, setVisible] = useState<boolean>(!!call)
  const [loading, setLoading] = useState<boolean>(false)
  const [showConfirmSendingDocuments, setShowConfirmSendingDocuments] =
    useState<boolean>(false)

  let iframeRef = useRef<HTMLIFrameElement>(null)

  useEffect(() => {
    setVisible(!!call)
    if (call) {
      handlePrintDocument(iframeRef, setLoading, setPrinted)
      dispatch(
        actions.requestDocument(
          call.isTle
            ? "teleexpertises"
            : call.isConsultation
            ? "consultations"
            : "calls",
          call.id,
        ),
      )
    } else {
      setLoading(true)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call])

  // Reset "printed" whenever a new document URL comes in
  useEffect(() => {
    if (documentUrl) {
      setPrinted(false)
    }
  }, [documentUrl])
  // Cleanup documentUrl when modal closes
  useEffect(() => {
    if (!visible && documentUrl) {
      if (documentUrl.startsWith("blob:")) URL.revokeObjectURL(documentUrl)
      dispatch(actions.success(undefined)) // clear redux state
      setPrinted(false)
      setLoading(false)
    }
  }, [visible, documentUrl, dispatch])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    let ticks = 0
    const interval = setInterval(() => {
      ticks += 1
      if (ticks > MAX_PRINT_INTERVAL_TICKS) {
        clearInterval(interval)
        return
      }
      try {
        if (documentUrl && documentUrl !== DOCUMENT_NOT_FOUND_PAGE) {
          iframeRef?.current?.contentWindow?.print()
          setPrinted(true)
        }
      } catch (error) {
        console.error(error, {
          path: "document::interval::print",
        })
      }
    }, 5000)
    setPrintInterval(interval)

    return () => clearInterval(interval)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    // if normal print behavior didn't work, try to print anyway after 5 seconds
    if (printed) {
      clearInterval(printInterval)
    }
    return () => clearInterval(printInterval)
  }, [printed, printInterval])

  const handleSendDocumentsToPharmacy = () => {
    dispatch(actions.sendDocumentsRequest({ id: callId }))
  }
  const ConfirmSendingDocuments = (props: any) => (
    <div>
      <Row justify="center">
        <h2 className="primary-color">
          <b>{languages.confirmTheSendingOfYourDocuments}</b>
        </h2>
        <Buttons style={{ paddingBottom: "100px" }}>
          <Button
            wide="long"
            loading={props.loading}
            disabled={props.loading}
            onClick={() => {
              handleSendDocumentsToPharmacy()
              setShowConfirmSendingDocuments(false)
            }}
          >
            {languages.yes}
          </Button>
          <Button
            wide="long"
            onClick={() => setShowConfirmSendingDocuments(false)}
          >
            {languages.no}
          </Button>
        </Buttons>
      </Row>
      <p
        className="primary-color"
        style={{ position: "absolute", bottom: "-20px" }}
      >
        *{languages.referrerIsAHealthProfessional}
      </p>
    </div>
  )

  const Actions = (props: any) => (
    <>
      <Buttons>
        <Button
          loading={props.loading}
          type="primary"
          disabled={props.loading}
          onClick={() => {
            if (printed) {
              setPrintedModal(true)
            } else {
              handlePrintDocument(iframeRef, setLoading, setPrinted)
            }
          }}
        >
          {printed ? languages.rePrint : languages.print}
        </Button>
        {!hideSendButton && (
          <Button
            loading={props.loading}
            disabled={props.loading || !props.canSendEmail}
            onClick={() => setShowConfirmSendingDocuments(true)}
          >
            {props.canSendEmail
              ? languages.sendDocumentsToTheReferrer
              : languages.sent}
          </Button>
        )}
        <Button
          danger
          type="primary"
          loading={props.loading}
          disabled={props.loading}
          onClick={rest.onAccept}
        >
          {languages.finishAndDisconnect}
        </Button>
      </Buttons>
      <GenericModal
        visible={printedModal}
        title={languages.rePrintTitle}
        acceptText={languages.no}
        acceptButtonProps={{ danger: false, type: "default" }}
        onAccept={() => setPrintedModal(false)}
        cancelText={languages.yes}
        cancelButtonProps={{ danger: true, type: "primary" }}
        onCancel={() => {
          setPrintedModal(false)
          handlePrintDocument(iframeRef, setLoading, setPrinted)
        }}
      >
        {languages.rePrintContent}
      </GenericModal>
    </>
  )

  return (
    <GenericModal
      {...rest}
      title={languages.yourDocuments}
      loading={loading}
      footer={
        rest.footer ||
        (!showConfirmSendingDocuments ? (
          <Actions {...props} />
        ) : (
          <ConfirmSendingDocuments {...props} />
        ))
      }
      visible={visible}
    >
      {!showConfirmSendingDocuments ? (
        <ReadableDocument
          documentUrl={documentUrl}
          ref={iframeRef}
          setPrinted={(isPrinted) => setPrinted(isPrinted)}
          isPrinted={printed}
        />
      ) : (
        <></>
      )}
    </GenericModal>
  )
}

export const ModalCheckup = ({
  visible,
  relativeId,
  vitals,
  ...rest
}: Omit<Partial<ModalProps>, "visible"> &
  Pick<ModalProps, "visible"> & {
  relativeId: number | null
  vitals?: Record<string, unknown>
}): JSX.Element => {
  const { languages } = useLanguage()
  const dispatch = useDispatch()
  const { documentUrl, ...props } = useSelector(
    ({
      client,
      documents,
    }: {
      client: ClientStore
      documents: DocumentsStore
    }) => ({
      documentUrl: client.documentUrl,
      ...documents,
    }),
  )
  const [printed, setPrinted] = useState<boolean>(false)
  const [printedModal, setPrintedModal] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [showConfirmSendingDocuments, setShowConfirmSendingDocuments] =
    useState<boolean>(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const hasCurrentDocument = useHasCheckupDocument({
    relative_id: relativeId,
    vitals,
  })
  const hasRetriedRef = useRef<boolean>(false)

  // The check-up document is generated when the patient reaches the end screen
  // (see CheckupEnd) and is kept for printing. Only request it again here if
  // that generation failed, at most once per opening so an API error does not
  // turn into a request loop.
  useEffect(() => {
    if (!visible) {
      hasRetriedRef.current = false
      return
    }
    if (!hasCurrentDocument && !props.loading && !hasRetriedRef.current) {
      hasRetriedRef.current = true
      setLoading(true)
      dispatch(
        actions.requestCheckupDocument({
          relative_id: relativeId,
          vitals: vitals ?? {},
        }),
      )
    }
  }, [visible, hasCurrentDocument, props.loading, relativeId, vitals, dispatch])

  useEffect(() => {
    if (documentUrl) {
      setPrinted(false)
      setLoading(false)
    }
  }, [documentUrl])

  // Stop the spinner when the generation failed so the patient can still finish
  useEffect(() => {
    if (props.message?.type === "error") {
      setLoading(false)
    }
  }, [props.message])

  const handleSendDocumentsToPharmacy = () => {
    dispatch(actions.sendCheckupDocumentsRequest({ relativeId: relativeId || null }))
  }

  const ConfirmSendingDocuments = (props: any) => (
    <div>
      <Row justify="center">
        <h2 className="primary-color">
          <b>{languages.confirmTheSendingOfYourDocuments}</b>
        </h2>
        <Buttons style={{ paddingBottom: "100px" }}>
          <Button
            wide="long"
            loading={props.loading}
            disabled={props.loading}
            onClick={() => {
              handleSendDocumentsToPharmacy()
              setShowConfirmSendingDocuments(false)
            }}
          >
            {languages.yes}
          </Button>
          <Button
            wide="long"
            onClick={() => setShowConfirmSendingDocuments(false)}
          >
            {languages.no}
          </Button>
        </Buttons>
      </Row>
      <p
        className="primary-color"
        style={{ position: "absolute", bottom: "-20px" }}
      >
        *{languages.referrerIsAHealthProfessional}
      </p>
    </div>
  )

  const Actions = (props: any) => (
    <>
      <Buttons>
        <div
          style={{
            display: "inline-grid",
            gridAutoFlow: "column",
            gridAutoColumns: "minmax(max-content, 1fr)",
            columnGap: "60px",
          }}
        >
          <Button
            loading={props.loading}
            disabled={props.loading}
            onClick={() => {
              if (printed) {
                setPrintedModal(true)
              } else {
                handlePrintDocument(iframeRef, setLoading, setPrinted)
              }
            }}
          >
            {printed ? languages.rePrint : languages.print}
          </Button>
          <Button
            loading={props.loading}
            disabled={props.loading || !props.canSendEmail}
            onClick={() => setShowConfirmSendingDocuments(true)}
          >
            {props.canSendEmail
              ? languages.sendDocumentsToTheReferrer
              : languages.sent}
          </Button>
          <Button
            type="primary"
            loading={props.loading}
            disabled={props.loading}
            onClick={rest.onAccept || rest.onCancel}
          >
            {languages.finish}
          </Button>
        </div>
      </Buttons>
      <GenericModal
        visible={printedModal}
        title={languages.rePrintTitle}
        acceptText={languages.no}
        acceptButtonProps={{ danger: false, type: "default" }}
        onAccept={() => setPrintedModal(false)}
        cancelText={languages.yes}
        cancelButtonProps={{ danger: true, type: "primary" }}
        onCancel={() => {
          setPrintedModal(false)
          handlePrintDocument(iframeRef, setLoading, setPrinted)
        }}
      >
        {languages.rePrintContent}
      </GenericModal>
    </>
  )

  return (
    <GenericModal
      {...rest}
      visible={visible}
      loading={loading || (!!props.loading && !hasCurrentDocument)}
      footer={
        !showConfirmSendingDocuments ? (
          <Actions {...props} />
        ) : (
          <ConfirmSendingDocuments {...props} />
        )
      }
    >
      {!showConfirmSendingDocuments ? (
        <ReadableDocument
          documentUrl={documentUrl}
          ref={iframeRef}
          setPrinted={(isPrinted) => setPrinted(isPrinted)}
          isPrinted={printed}
        />
      ) : (
        <></>
      )}
    </GenericModal>
  )
}
